import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldGroup } from "@/modules/shell/FieldGroup";
import {
  addPartyAddress,
  addPartyEmail,
  addPartyPhone,
  deletePartyAddress,
  deletePartyEmail,
  deletePartyPhone,
  patchPartyAddress,
  patchPartyEmail,
  patchPartyPhone,
  type PartyPhonebook,
} from "@/shared/api/parties";
import { notify } from "@/shared/feedback/notify";

const PHONE_LABELS = ["Mobile", "Work", "Home", "Fax", "Other"] as const;
const EMAIL_LABELS = ["Work", "Personal", "Other"] as const;
const ADDR_LABELS = ["Work", "Billing", "Shipping", "Home", "Other"] as const;

type Props = {
  companySlug: string;
  partyId: string;
  phonebook: PartyPhonebook;
  canEdit: boolean;
  onChanged: () => Promise<unknown> | void;
};

export function ContactPhonebook({
  companySlug,
  partyId,
  phonebook,
  canEdit,
  onChanged,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [phoneValue, setPhoneValue] = useState("");
  const [phoneLabel, setPhoneLabel] = useState<string>("Mobile");
  const [emailValue, setEmailValue] = useState("");
  const [emailLabel, setEmailLabel] = useState<string>("Work");
  const [addrLine1, setAddrLine1] = useState("");
  const [addrCity, setAddrCity] = useState("");
  const [addrProvince, setAddrProvince] = useState("");
  const [addrLabel, setAddrLabel] = useState<string>("Work");

  async function run(fn: () => Promise<unknown>, okMsg: string) {
    setBusy(true);
    try {
      await fn();
      notify.success(okMsg);
      await onChanged();
    } catch (err: unknown) {
      notify.error(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <FieldGroup
      title="Phonebook"
      className="max-w-lg space-y-6"
      description="Labeled phones, emails, and addresses — primary shows on docs"
    >
      <FieldGroup title="Phones" level="sub">
        {phonebook.phones.length === 0 ? (
          <p className="m-0 text-sm text-[var(--color-muted)]">No phones yet.</p>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0">
            {phonebook.phones.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm"
              >
                <span className="text-[var(--color-muted)]">{p.label}</span>
                <a href={`tel:${p.value}`} className="no-underline">
                  {p.value}
                </a>
                {p.isPrimary ? (
                  <span className="text-xs text-[var(--color-muted)]">
                    primary
                  </span>
                ) : canEdit ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () =>
                          patchPartyPhone(companySlug, partyId, p.id, {
                            isPrimary: true,
                          }),
                        "Primary phone updated",
                      )
                    }
                  >
                    Make primary
                  </Button>
                ) : null}
                {canEdit ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () => deletePartyPhone(companySlug, partyId, p.id),
                        "Phone removed",
                      )
                    }
                  >
                    Remove
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {canEdit ? (
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div className="space-y-1">
              <Label htmlFor="pb-phone-label">Label</Label>
              <select
                id="pb-phone-label"
                className="h-9 rounded-md border border-[var(--color-border)] bg-transparent px-2 text-sm"
                value={phoneLabel}
                onChange={(e) => setPhoneLabel(e.target.value)}
              >
                {PHONE_LABELS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[10rem] flex-1 space-y-1">
              <Label htmlFor="pb-phone">Number</Label>
              <Input
                id="pb-phone"
                inputMode="tel"
                value={phoneValue}
                onChange={(e) => setPhoneValue(e.target.value)}
                placeholder="+63…"
              />
            </div>
            <Button
              type="button"
              size="sm"
              disabled={busy || !phoneValue.trim()}
              onClick={() =>
                void run(async () => {
                  await addPartyPhone(companySlug, partyId, {
                    label: phoneLabel,
                    value: phoneValue.trim(),
                  });
                  setPhoneValue("");
                }, "Phone added")
              }
            >
              Add
            </Button>
          </div>
        ) : null}
      </FieldGroup>

      <FieldGroup title="Emails" level="sub">
        {phonebook.emails.length === 0 ? (
          <p className="m-0 text-sm text-[var(--color-muted)]">No emails yet.</p>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0">
            {phonebook.emails.map((e) => (
              <li
                key={e.id}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm"
              >
                <span className="text-[var(--color-muted)]">{e.label}</span>
                <a href={`mailto:${e.value}`} className="no-underline">
                  {e.value}
                </a>
                {e.isPrimary ? (
                  <span className="text-xs text-[var(--color-muted)]">
                    primary
                  </span>
                ) : canEdit ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () =>
                          patchPartyEmail(companySlug, partyId, e.id, {
                            isPrimary: true,
                          }),
                        "Primary email updated",
                      )
                    }
                  >
                    Make primary
                  </Button>
                ) : null}
                {canEdit ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () => deletePartyEmail(companySlug, partyId, e.id),
                        "Email removed",
                      )
                    }
                  >
                    Remove
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {canEdit ? (
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div className="space-y-1">
              <Label htmlFor="pb-email-label">Label</Label>
              <select
                id="pb-email-label"
                className="h-9 rounded-md border border-[var(--color-border)] bg-transparent px-2 text-sm"
                value={emailLabel}
                onChange={(e) => setEmailLabel(e.target.value)}
              >
                {EMAIL_LABELS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[12rem] flex-1 space-y-1">
              <Label htmlFor="pb-email">Email</Label>
              <Input
                id="pb-email"
                type="email"
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                placeholder="name@company.com"
              />
            </div>
            <Button
              type="button"
              size="sm"
              disabled={busy || !emailValue.trim()}
              onClick={() =>
                void run(async () => {
                  await addPartyEmail(companySlug, partyId, {
                    label: emailLabel,
                    value: emailValue.trim(),
                  });
                  setEmailValue("");
                }, "Email added")
              }
            >
              Add
            </Button>
          </div>
        ) : null}
      </FieldGroup>

      <FieldGroup title="Addresses" level="sub">
        {phonebook.addresses.length === 0 ? (
          <p className="m-0 text-sm text-[var(--color-muted)]">
            No addresses yet.
          </p>
        ) : (
          <ul className="m-0 list-none space-y-3 p-0">
            {phonebook.addresses.map((a) => (
              <li key={a.id} className="space-y-1 text-sm">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-[var(--color-muted)]">{a.label}</span>
                  {a.isPrimary ? (
                    <span className="text-xs text-[var(--color-muted)]">
                      primary
                    </span>
                  ) : canEdit ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () =>
                            patchPartyAddress(companySlug, partyId, a.id, {
                              isPrimary: true,
                            }),
                          "Primary address updated",
                        )
                      }
                    >
                      Make primary
                    </Button>
                  ) : null}
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () =>
                            deletePartyAddress(companySlug, partyId, a.id),
                          "Address removed",
                        )
                      }
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
                <div>{a.composed}</div>
                {a.mapsUrl ? (
                  <a
                    href={a.mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm no-underline"
                  >
                    Open in Maps
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {canEdit ? (
          <div className="space-y-2 border-t border-[var(--color-border)]/60 pt-3">
            <div className="flex flex-wrap gap-2">
              <div className="space-y-1">
                <Label htmlFor="pb-addr-label">Label</Label>
                <select
                  id="pb-addr-label"
                  className="h-9 rounded-md border border-[var(--color-border)] bg-transparent px-2 text-sm"
                  value={addrLabel}
                  onChange={(e) => setAddrLabel(e.target.value)}
                >
                  {ADDR_LABELS.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-[12rem] flex-1 space-y-1">
                <Label htmlFor="pb-addr-line1">Street</Label>
                <Input
                  id="pb-addr-line1"
                  value={addrLine1}
                  onChange={(e) => setAddrLine1(e.target.value)}
                  placeholder="Street / building"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="min-w-[8rem] flex-1 space-y-1">
                <Label htmlFor="pb-addr-city">City</Label>
                <Input
                  id="pb-addr-city"
                  value={addrCity}
                  onChange={(e) => setAddrCity(e.target.value)}
                />
              </div>
              <div className="min-w-[8rem] flex-1 space-y-1">
                <Label htmlFor="pb-addr-prov">Province</Label>
                <Input
                  id="pb-addr-prov"
                  value={addrProvince}
                  onChange={(e) => setAddrProvince(e.target.value)}
                />
              </div>
              <Button
                type="button"
                size="sm"
                className="self-end"
                disabled={busy || !addrLine1.trim()}
                onClick={() =>
                  void run(async () => {
                    await addPartyAddress(companySlug, partyId, {
                      label: addrLabel,
                      line1: addrLine1.trim(),
                      city: addrCity.trim() || null,
                      province: addrProvince.trim() || null,
                      countryCode: "PH",
                    });
                    setAddrLine1("");
                    setAddrCity("");
                    setAddrProvince("");
                  }, "Address added")
                }
              >
                Add
              </Button>
            </div>
          </div>
        ) : null}
      </FieldGroup>
    </FieldGroup>
  );
}
